import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';
import * as api from '../services/api';
import DishImageSplash from './common/DishImageSplash';
import './SearchBarUpdated.css';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const ResultImage = ({ src, alt, fallbackEmoji }) => {
  const [error, setError] = useState(false);
  if (!src || error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-namke-cream">
        <span className="text-2xl">{fallbackEmoji}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setError(true)}
    />
  );
};

const SearchBarUpdated = ({ onSearchResults, placeholder }) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('searchBar.placeholder');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [availableCities, setAvailableCities] = useState([]);
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const cityPopupRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastRequestedQueryRef = useRef('');

  const filteredCities = cityQuery.trim()
    ? availableCities.filter((c) => c.toLowerCase().includes(cityQuery.trim().toLowerCase()))
    : availableCities;

  // Charger la liste des villes disponibles (déduite des restaurants) pour le filtre
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.getRestaurants();
        const list = Array.isArray(response) ? response : (response?.restaurants || []);
        const cities = [...new Set(list.map((r) => r.city).filter(Boolean))].sort();
        if (!cancelled) setAvailableCities(cities);
      } catch (error) {
        console.error('Erreur lors du chargement des villes:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const normalized = query.trim();
      if (normalized.length >= 2) {
        const requestKey = `${normalized}|${city}`;
        if (requestKey === lastRequestedQueryRef.current) return;
        lastRequestedQueryRef.current = requestKey;
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        performSearch(normalized, controller.signal);
      } else {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        lastRequestedQueryRef.current = '';
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, city]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Close results/popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
      if (cityPopupRef.current && !cityPopupRef.current.contains(event.target)) {
        setShowCityPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Perform search
  const performSearch = async (searchQuery, signal) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const cityFilter = city ? { city } : {};
      const [restaurantsResults, dishesResults] = await Promise.all([
        api.searchRestaurants(searchQuery, { limit: 5, ...cityFilter }, signal ? { signal } : {}),
        api.searchDishes(searchQuery, { limit: 5, ...cityFilter }, signal ? { signal } : {})
      ]);

      const combinedResults = [
        ...(restaurantsResults || []).map(item => ({
          ...item,
          type: 'restaurant',
          displayName: item.name,
          displayImage: api.resolveRestaurantImagePath(item)
        })),
        ...(dishesResults || []).map(item => ({
          ...item,
          type: 'dish',
          displayName: item.name,
          displayImage: api.resolveDishImagePath(item),
          category: item.category,
          restaurant_id: item.restaurant_id || item.restaurant?.id || null
        }))
      ].slice(0, 5); // 5 suggestions max

      setSearchResults(combinedResults);
      setShowResults(true);
      setSelectedIndex(-1);

      if (onSearchResults) {
        onSearchResults(combinedResults);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Soumettre la recherche et afficher tous les résultats sur la Home (comme la navbar)
  const goToFullSearchResults = () => {
    const q = query.trim();
    if (q) {
      setShowResults(false);
      setQuery('');
      setSelectedIndex(-1);
      const citySuffix = city ? `&city=${encodeURIComponent(city)}` : '';
      navigate(`/?q=${encodeURIComponent(q)}${citySuffix}`);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim()) {
        if (showResults && searchResults.length > 0 && selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleResultClick(searchResults[selectedIndex]);
        } else {
          goToFullSearchResults();
        }
      }
      return;
    }
    if (e.key === 'Escape') {
      setShowResults(false);
      setSelectedIndex(-1);
      return;
    }
    if (!showResults || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      default:
        break;
    }
  };

  // Handle result click
  const handleResultClick = (result) => {
    setShowResults(false);
    setQuery('');
    setSelectedIndex(-1);
    
    // Scroll to top before navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Navigate to the appropriate page using React Router
    if (result.type === 'restaurant') {
      navigate(`/restaurant/${result.id}`);
    } else if (result.type === 'dish') {
      // Navigate to product page for dish
      if (result.id) {
        navigate(`/product/${result.id}`);
      } else {
        console.error('ID manquant pour le plat:', result);
        alert('Impossible de trouver le plat');
      }
    }
  };

  // Clear search
  const clearSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    lastRequestedQueryRef.current = '';
    setQuery('');
    setSearchResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
  };

  // Get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return api.getImageUrl(imagePath);
  };

  return (
    <div className="relative group" ref={searchRef} style={{ zIndex: 10 }}>
      {/* Pill de recherche unique — icône + input + séparateur + quartier + bouton */}
      <form
        onSubmit={(e) => { e.preventDefault(); goToFullSearchResults(); }}
        className="w-full flex items-center gap-2 sm:gap-2.5 bg-namke-white rounded-[28px] sm:rounded-full pl-5 sm:pl-6 pr-2.5 py-2.5 sm:py-2 shadow-[0_14px_34px_rgba(5,16,4,0.1)] flex-wrap sm:flex-nowrap"
      >
        <FaSearch className="h-5 w-5 shrink-0" style={{ color: PRIMARY }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowResults(true)}
          placeholder={resolvedPlaceholder}
          className="flex-1 min-w-[140px] py-2.5 sm:py-2 bg-transparent text-base sm:text-[17px] text-namke-black placeholder-namke-black/45 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="shrink-0 p-1 rounded-full hover:bg-namke-cream transition-colors"
          >
            <FaTimes className="h-4 w-4 text-namke-black/40" />
          </button>
        )}

        {availableCities.length > 0 && (
          <>
            <span className="hidden sm:block w-px h-6 bg-namke-black/10 shrink-0" />
            <div className="relative shrink-0" ref={cityPopupRef}>
              <button
                type="button"
                onClick={() => {
                  setShowCityPopup((prev) => !prev);
                  setCityQuery('');
                }}
                className="flex items-center gap-2 text-sm font-medium text-namke-black hover:opacity-80 transition-opacity px-1"
                aria-haspopup="listbox"
                aria-expanded={showCityPopup}
              >
                <FaMapMarkerAlt className="h-4 w-4 shrink-0" style={{ color: TERRA }} />
                <span className="truncate max-w-[110px]">{city || t('searchBar.neighborhood')}</span>
              </button>

              {showCityPopup && (
                <div className="absolute z-30 w-72 mt-2 rounded-2xl shadow-xl bg-white right-0 sm:left-0 overflow-hidden">
                  <div className="p-2 border-b border-namke-black/10">
                    <input
                      type="text"
                      autoFocus
                      value={cityQuery}
                      onChange={(e) => setCityQuery(e.target.value)}
                      placeholder={t('common.searchCityPlaceholder')}
                      className="w-full px-3 py-2 text-sm bg-namke-cream/50 rounded-full focus:outline-none focus:ring-2 focus:ring-[#054625]"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    <button
                      type="button"
                      onClick={() => { setCity(''); setShowCityPopup(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-namke-cream/60 transition-colors ${!city ? 'font-semibold text-namke-black' : 'text-namke-black/70'}`}
                    >
                      {t('common.allCities')}
                    </button>
                    {filteredCities.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-namke-black/45">{t('common.noCityFound')}</p>
                    ) : (
                      filteredCities.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => { setCity(c); setShowCityPopup(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-namke-cream/60 transition-colors ${city === c ? 'font-semibold text-namke-black' : 'text-namke-black/70'}`}
                        >
                          {c}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <button
          type="submit"
          className="shrink-0 text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors"
          style={{ backgroundColor: TERRA }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#CD3A16'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = TERRA; }}
        >
          {t('searchBar.searchButton')}
        </button>
      </form>

      {/* Search Results Dropdown */}
      {showResults && (searchResults.length > 0 || loading) && (
        <div
          ref={resultsRef}
          className="search-results-dropdown absolute z-[20] w-full mt-2 rounded-2xl shadow-xl max-h-96 overflow-y-auto bg-white"
          style={{ top: '100%', left: 0, right: 0 }}
        >
          {loading ? (
            <div className="p-4 text-center text-namke-black/60">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-namke-cream border-t-[rgb(5,70,37)] mx-auto"></div>
              <p className="mt-2">{t('common.searching')}</p>
            </div>
          ) : (
            <div className="py-2">
              {searchResults.map((result, index) => (
                <div
                  key={`${result.type}-${result.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleResultClick(result);
                  }}
                  className={`search-results-item px-4 py-3 cursor-pointer hover:bg-namke-cream/50 transition-colors border-b border-namke-black/5 last:border-b-0 ${
                    index === selectedIndex ? 'bg-namke-cream/50' : ''
                  }`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleResultClick(result);
                    }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {/* Image */}
                    <div className="flex-shrink-0 w-12 h-12 bg-namke-cream rounded-lg overflow-hidden">
                      {result.type === 'dish' ? (
                        <DishImageSplash
                          src={result.displayImage ? getImageUrl(result.displayImage) : null}
                          alt={result.displayName}
                          dishName={result.displayName}
                          category={result.category?.name || result.category}
                          className="w-full h-full"
                          showLoader={true}
                        />
                      ) : (
                        <ResultImage
                          src={result.displayImage ? getImageUrl(result.displayImage) : null}
                          alt={result.displayName}
                          fallbackEmoji="🏪"
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-namke-black truncate">
                          {result.displayName}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(5, 70, 37, 0.08)', color: PRIMARY }}>
                          {result.type === 'restaurant' ? t('common.restaurant') : t('common.dish')}
                        </span>
                      </div>

                      {result.type === 'restaurant' ? (
                        <div className="flex items-center space-x-2 mt-1">
                          <FaMapMarkerAlt className="text-namke-black/35 text-xs" />
                          <p className="text-xs text-namke-black/55 truncate">
                            {result.address}, {result.city}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-namke-black/55 truncate">
                            {result.restaurant_name || result.restaurant?.name || t('common.restaurant')}
                          </p>
                          <span className="text-xs font-medium text-namke-black">
                            {result.price ? api.formatPrice(result.price) : t('searchBar.notAvailable')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Arrow indicator */}
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-namke-black/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
              <div className="p-3 border-t border-namke-black/5">
                <button
                  type="button"
                  onClick={goToFullSearchResults}
                  className="w-full text-center font-medium text-sm py-2"
                  style={{ color: PRIMARY }}
                >
                  {t('common.seeAllResultsFor', { query })}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBarUpdated;
